/**
 * HISTORY — the fifth tab of the device page.
 *
 * What it answers: how did this device get the software it is running, and what
 * has anyone done to it lately. Three cards, in the mockup's order — Installs,
 * Version ledger, Support actions — then the link across to the unit page.
 *
 * 🔴 ONLY THE LAST INSTALL IS STORED. `unit.osInstall` is a single stamp that a
 * re-install OVERWRITES; there is no append-only install log anywhere yet. The
 * mockup shows three timeline entries to illustrate the shape the log will take
 * — it is not data that exists. So this renders the ONE install the row
 * actually carries and says plainly that the earlier ones were never recorded.
 * Rendering a fabricated multi-entry timeline would be inventing history about
 * real hardware, which is worse than an honest gap.
 *
 * The two lists that DO have real history are embedded at the platform's 5-row
 * cap, each with a link to the full view that already exists rather than a
 * second copy of it:
 *   - the version ledger → `DeviceHistoryModal` (Phase C ledger, deduped)
 *   - support actions    → `CommandHistory` + `AuditView` (Task 23's audit)
 * Neither is re-implemented here.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Compass } from 'lucide-react';
import { deviceConfigApi } from '../../../services/deviceConfigApi';
import { dedupeLedgerEntries, type FleetLedgerEntry } from '../fleet/fleetRows';
import { useDeviceCommands } from '../fleet/useDeviceCommands';
import CommandHistory from '../fleet/CommandHistory';
import AuditView from '../fleet/AuditView';
import DeviceHistoryModal from '../fleet/DeviceHistoryModal';
import { type DeviceTabProps, deviceTitle, formatWhen, relativeTime, unitHref } from './deviceDetail';
import { RubyLoader } from '../../../components/RubyLoader';

/** Embedded lists cap at 5 and link out — the platform rule, not a local choice. */
const CAP = 5;
/** Read a little past the cap so "View all (n)" tells the truth about how many there are. */
const LEDGER_LIMIT = 100;

const SOURCE_LABEL: Record<string, string> = {
  local: 'from a local build',
  dev: 'from dev',
  prod: 'from prod',
};

function Card({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-stone-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 px-5 pt-4">
        <h2 className="text-[14.5px] font-bold tracking-tight text-stone-900">{title}</h2>
        {aside ? <div className="ml-auto">{aside}</div> : null}
      </div>
      <div className="px-5 pb-5 pt-2">{children}</div>
    </section>
  );
}

function ViewAll({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-h-[44px] px-1 text-[12.5px] font-semibold hover:underline"
      style={{ color: '#DC143C' }}
    >
      {label}
    </button>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <p className="mt-3 text-[11.5px] text-stone-400">{children}</p>;
}

export default function HistoryTab({ row, model, onRefresh }: DeviceTabProps) {
  void onRefresh; // History only reads; the shell owns refreshing.

  const [entries, setEntries] = useState<FleetLedgerEntry[] | null>(null);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [showLedger, setShowLedger] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const { deviceId, deviceType } = row;
  const { commands, loading: commandsLoading, error: commandsError } = useDeviceCommands(deviceId, deviceType);

  useEffect(() => {
    // The ledger is keyed by BOTH — `deviceId` is only unique within a type, so
    // without the type there is nothing safe to ask for.
    if (!deviceId || !deviceType) { setEntries([]); return; }
    let live = true;
    setEntries(null);
    setLedgerError(null);
    deviceConfigApi
      .listDeviceLedger(deviceType, deviceId, LEDGER_LIMIT)
      .then((r) => {
        if (!live) return;
        // Phase C records the same transition twice on overlapping check-ins;
        // the API docblock says to dedupe at read time, and this does.
        setEntries(dedupeLedgerEntries((r?.entries || []) as FleetLedgerEntry[]));
      })
      .catch((e: any) => { if (live) setLedgerError(e?.message || 'Could not load the version history'); });
    return () => { live = false; };
  }, [deviceId, deviceType]);

  const href = unitHref(row.unitId);
  const shownLedger = (entries || []).slice(0, CAP);

  const installBuild = row.lastKnownInstallBuild || row.installTarget;
  const installWhen = formatWhen(row.installedAt);
  const hasInstall = !!row.installedAt || !!installBuild || !!row.installedBy;

  return (
    <div className="flex flex-col gap-4">
      {/* ── INSTALLS ─────────────────────────────────────────────────────── */}
      <Card title="Installs">
        {hasInstall ? (
          <ol className="relative border-l border-stone-200 pl-4">
            <li data-testid="install-entry" className="relative">
              <span
                aria-hidden="true"
                className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full"
                style={{ background: '#DC143C' }}
              />
              <div className="text-[13.5px] font-bold text-stone-900">
                {installBuild ? `Installed · ${installBuild}` : 'Installed'}
              </div>
              <div className="mt-0.5 text-[12px] text-stone-500">
                {[
                  installWhen || relativeTime(row.installedAt) || 'Date not recorded',
                  row.installedBy || null,
                  row.installSource ? SOURCE_LABEL[row.installSource] || row.installSource : null,
                ].filter(Boolean).join(' · ')}
              </div>
              {row.installVersionCode ? (
                <div className="mt-0.5 font-mono text-[11px] text-stone-400">build {row.installVersionCode}</div>
              ) : null}
            </li>
          </ol>
        ) : (
          <p className="py-2 text-[12.5px] text-stone-400">No install recorded for this device.</p>
        )}
        {/*
          The honest line the mockup's three-entry illustration stands in for.
          One stamp, overwritten each time — see the file header.
        */}
        <Muted>Only the most recent install is kept — earlier installs are not recorded yet.</Muted>
      </Card>

      {/* ── VERSION LEDGER ───────────────────────────────────────────────── */}
      <Card
        title="Version ledger"
        aside={
          entries && entries.length > 0 && deviceId && deviceType ? (
            <ViewAll
              label={entries.length > CAP ? `View all (${entries.length})` : 'View all'}
              onClick={() => setShowLedger(true)}
            />
          ) : null
        }
      >
        {ledgerError ? (
          <div role="alert" className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12.5px] text-rose-700">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            Could not load the version history — {ledgerError}
          </div>
        ) : entries === null ? (
          <RubyLoader variant="section" label="Loading…" />
        ) : entries.length === 0 ? (
          <p className="py-2 text-[12.5px] text-stone-400">No version changes recorded yet.</p>
        ) : (
          <ol className="flex flex-col">
            {shownLedger.map((e, i) => (
              <li
                key={`${e.at}-${i}`}
                data-testid="ledger-entry"
                className="flex items-baseline justify-between gap-4 border-b border-stone-100 py-2.5 last:border-b-0 last:pb-0"
              >
                <span className="font-mono text-[12px] text-stone-900">
                  {e.fromVersion ? `${e.fromVersion} → ${e.toVersion}` : `First seen · ${e.toVersion}`}
                </span>
                <span className="shrink-0 text-[11.5px] text-stone-400">
                  {relativeTime(e.at) || formatWhen(e.at) || '—'}
                </span>
              </li>
            ))}
          </ol>
        )}
      </Card>

      {/* ── SUPPORT ACTIONS ──────────────────────────────────────────────────
          CommandHistory already caps at 5 and carries the View-all link; it is
          reused whole rather than restyled into a second table. */}
      <Card
        title="Support actions"
        aside={deviceId ? <ViewAll label="Open audit trail" onClick={() => setShowAudit(true)} /> : null}
      >
        {commandsError ? (
          <div role="alert" className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12.5px] text-rose-700">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            Could not load support actions — {commandsError}
          </div>
        ) : commandsLoading ? (
          <RubyLoader variant="section" label="Loading…" />
        ) : (
          <CommandHistory commands={commands} onViewAll={() => setShowAudit(true)} />
        )}
      </Card>

      {/* ── WHERE THIS TAB STOPS ─────────────────────────────────────────────
          The unit page owns the hardware life and already renders a timeline
          for it. Two timelines over one device would have to be kept in
          agreement, and they would drift. No unit = no link, never a dead one. */}
      {href ? (
        <Link
          to={href}
          className="flex min-h-[44px] items-center gap-4 rounded-2xl border border-stone-200 bg-white px-5 py-4 shadow-sm hover:border-stone-300"
        >
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
            style={{ background: 'rgba(220,20,60,0.08)', color: '#DC143C' }}
          >
            <Compass size={20} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-bold text-stone-900">Looking for the rest of the story?</span>
            <span className="block text-[12.5px] text-stone-500">
              Order, provisioning, condition, damage and the hardware journey live on the unit page
            </span>
          </span>
          <span className="shrink-0 text-[13px] font-semibold" style={{ color: '#DC143C' }}>Open unit →</span>
        </Link>
      ) : null}

      {showLedger && deviceId && deviceType ? (
        <DeviceHistoryModal
          deviceType={deviceType}
          deviceId={deviceId}
          deviceLabel={deviceTitle(row, model)}
          onClose={() => setShowLedger(false)}
        />
      ) : null}

      {showAudit && deviceId ? (
        <AuditView
          deviceId={deviceId}
          registerName={deviceTitle(row, model)}
          onClose={() => setShowAudit(false)}
        />
      ) : null}
    </div>
  );
}
